import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useSearch, Link } from '@tanstack/react-router';
import { CheckCircle2, AlertCircle, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api, formatCurrency, formatDate } from '@/lib/utils';
import { BRAND } from '@maintenance-tracker/shared';
import { BrandLogo } from '@/components/brand/brand-logo';

interface PayInfo {
  businessName: string;
  planName: string;
  amount: number;
  currency: string;
  endDate: string | null;
  status: 'ready' | 'expired' | 'invalid' | 'paid';
  contactName?: string;
}

export function PayPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const search = useSearch({ strict: false }) as { success?: string; cancelled?: string };

  const { data, isLoading, error } = useQuery({
    queryKey: ['pay', token],
    queryFn: () => api<PayInfo>(`/api/v1/pay/${token}`),
    retry: false,
  });

  const checkout = useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/api/v1/pay/${token}/checkout`, { method: 'POST', body: '{}' }),
    onSuccess: (res) => {
      if (res.url) window.location.href = res.url;
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-app">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  const status = search.success === '1' ? 'paid' : data?.status ?? 'invalid';

  return (
    <div className="flex min-h-screen flex-col bg-bg-app">
      <header className="border-b border-border bg-bg-surface px-4 py-4">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
          <BrandLogo size="sm" to="/" showName />
          <span className="shrink-0 text-xs text-text-muted">Secure renewal payment</span>
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-10">
        {status === 'ready' && data ? (
          <Card title="Renew your plan" description={`${data.businessName} maintenance agreement`}>
            <div className="space-y-5">
              <div className="rounded-xl border border-border bg-bg-muted p-4">
                <p className="text-sm text-text-secondary">Plan</p>
                <p className="text-lg font-semibold text-text-primary">{data.planName}</p>
                {data.endDate && (
                  <p className="mt-2 text-sm text-text-secondary">
                    Current term ends {formatDate(data.endDate)}
                  </p>
                )}
                <p className="mt-4 text-3xl font-semibold tracking-tight text-text-primary">
                  {formatCurrency(data.amount)}
                </p>
              </div>
              <Button
                className="w-full"
                size="lg"
                onClick={() => checkout.mutate()}
                loading={checkout.isPending}
              >
                <CreditCard className="h-4 w-4" /> Pay &amp; renew
              </Button>
              <p className="text-center text-xs text-text-muted">
                Payments are processed securely by Stripe. Funds go directly to {data.businessName}.
              </p>
            </div>
          </Card>
        ) : status === 'paid' ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-success" />
            <h1 className="mt-4 text-xl font-semibold">Payment received</h1>
            <p className="mt-2 text-sm text-text-secondary">
              Your renewal with {data?.businessName ?? 'this business'} is complete. You&apos;ll receive a confirmation email shortly.
            </p>
          </div>
        ) : status === 'expired' ? (
          <div className="text-center">
            <AlertCircle className="mx-auto h-14 w-14 text-warning" />
            <h1 className="mt-4 text-xl font-semibold">Link expired</h1>
            <p className="mt-2 text-sm text-text-secondary">
              This renewal link is no longer valid. Contact {data?.businessName ?? 'the business'} to request a new one.
            </p>
          </div>
        ) : (
          <div className="text-center">
            <AlertCircle className="mx-auto h-14 w-14 text-text-muted" />
            <h1 className="mt-4 text-xl font-semibold">Invalid link</h1>
            <p className="mt-2 text-sm text-text-secondary">
              {error ? 'We could not load this payment page.' : 'This payment link is not valid.'}
            </p>
            <Link to="/" className="mt-6 inline-block text-sm text-brand hover:underline">
              Go to {BRAND.name}
            </Link>
          </div>
        )}

        {search.cancelled === '1' && status === 'ready' && (
          <p className="mt-4 text-center text-sm text-text-secondary">Payment cancelled — you can try again when ready.</p>
        )}
      </main>
    </div>
  );
}
